/**
 * Custom Tag Editor Component
 *
 * Features:
 * - contenteditable based rich text editor
 * - Tag insertion with hierarchical tree selector
 * - Inline tag editing (double-click)
 * - Sidebar with inserted tags
 * - Copy-paste with tag preservation
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms'
import { CommonModule } from '@angular/common'
import { IonicModule } from '@ionic/angular'
import { TreeModule } from 'primeng/tree'
import { Listbox, ListboxModule } from 'primeng/listbox'
import { TreeNode } from 'primeng/api'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { Subject } from 'rxjs'
import { debounce } from '../../../../shared/helpers/debounce'
import { EditorInteractionService } from './services/editor-interaction.service'
import { FormattingService } from './services/formatting.service'
import { PasteService } from './services/paste.service'
import { TagManagerService } from './services/tag-manager.service'
import { DebugLoggingService } from './services/debug-logging.service'
import { TagEditorStateService } from './services/tag-editor-state.service'
import { EditorContext, EventCallbacks, TagEditorEventService } from './services/tag-editor-event.service'
import { ContentCallbacks, TagEditorContentService } from './services/tag-editor-content.service'
import { ChangeCallbacks, TagEditorChangeService } from './services/tag-editor-change.service'
import { InlineEditCallbacks, TagEditorInlineEditService } from './services/tag-editor-inline-edit.service'
import { TagEditorUiStateService } from './services/tag-editor-ui-state.service'
import { SelectorCallbacks, TagEditorSelectorService } from './services/tag-editor-selector.service'
import { InsertionCallbacks, TagEditorInsertionService } from './services/tag-editor-insertion.service'
import { TagEditorTreeNode } from './tag-editor-tree-node'
import { EditorContentChangeEvent, FormattingState, InsertedTagCustom } from './tag-editor.model'
import {
  COMPONENT_STRINGS,
  CSS_STYLE_VALUES,
  HTML_VALUES,
  TAG_EDITOR_DELAY,
  TAG_EDITOR_STRINGS,
  TAG_EDITOR_THRESHOLDS,
  TAG_EDITOR_VALUES,
} from './tag-editor.constants'

@Component({
  selector: 'app-tag-editor',
  templateUrl: './tag-editor.component.html',
  styleUrls: ['./tag-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, IonicModule, TreeModule, ListboxModule, TranslocoModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TagEditorComponent),
      multi: true,
    },
  ],
})
export class TagEditorComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator {
  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  @Input() treeNodes: ReadonlyArray<TagEditorTreeNode> = []
  @Input() placeholder: string = ''
  @Input() public debug: boolean = false
  @Input() clientId: number | null = null
  @Input() initialContent: string = ''
  @Input() requireTags: boolean = false

  @Output() valueChange = new EventEmitter<string>()
  @Output() tagDataChange = new EventEmitter<EditorContentChangeEvent>()

  @ViewChild('editorContent', { static: true })
  private readonly editorContentRef!: ElementRef<HTMLDivElement>

  @ViewChild('tagListbox', { static: false })
  private tagListbox: Listbox | undefined

  protected value: string = ''
  protected onChange: (value: string) => void = () => {}
  protected onTouched: () => void = () => {}
  protected disabled: boolean = false

  public insertedTags: ReadonlyArray<InsertedTagCustom> = []
  public selectedTag: InsertedTagCustom | null = null

  protected get insertedTagsArray(): ReadonlyArray<InsertedTagCustom> {
    return this.insertedTags
  }

  protected showTagSelector: boolean = false
  protected formattingState: FormattingState = {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isOrderedList: false,
    isUnorderedList: false,
    hasLink: false,
  }
  public isTextSelected: boolean = false
  private isSelectingText: boolean = false

  private readonly destroy$ = new Subject<void>()
  private readonly debouncedEmitChange!: (content: string) => void

  private cleanupEventListeners: (() => void) | null = null

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly editorInteractionService: EditorInteractionService,
    private readonly formattingService: FormattingService,
    private readonly pasteService: PasteService,
    private readonly tagManagerService: TagManagerService,
    private readonly debugLoggingService: DebugLoggingService,
    private readonly stateService: TagEditorStateService,
    private readonly translocoService: TranslocoService,
    private readonly eventService: TagEditorEventService,
    private readonly contentService: TagEditorContentService,
    private readonly changeService: TagEditorChangeService,
    private readonly inlineEditService: TagEditorInlineEditService,
    private readonly uiStateService: TagEditorUiStateService,
    private readonly selectorService: TagEditorSelectorService,
    private readonly insertionService: TagEditorInsertionService,
  ) {
    // Debounced change emitter
    this.debouncedEmitChange = debounce((content: string) => {
      this.emitChange(content)
    }, TAG_EDITOR_DELAY.DEBOUNCE_INPUT_MS)
  }

  // ========== Lifecycle Hooks ==========

  ngOnInit(): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, 'ngOnInit', {
      debug: this.debug,
    })

    // This ensures clean state when opening detail/form
    this.stateService.clearState()
    this.showTagSelector = false
    this.selectedTag = null
    this.insertedTags = []

    // Always enable debug logging for selection tracking (critical for troubleshooting)
    this.debugLoggingService.setDebugEnabled(true)

    // Initialize editor immediately if ViewChild is available
    if (this.editorContentRef) {
      this.debugLoggingService.logWithService(
        COMPONENT_STRINGS.COMPONENT_NAME,
        'Editor ref available in ngOnInit, initializing immediately',
      )
      this.initializeEditor()
    } else {
      // Fallback: initialize after view is ready
      setTimeout(() => {
        this.initializeEditor()
      }, TAG_EDITOR_VALUES.DEFAULT_TIMEOUT_MS)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const initialContentChange = changes['initialContent']
    if (initialContentChange) {
      const newContent: string = initialContentChange.currentValue as string
      if (newContent && this.editorContentRef) {
        // Prevent infinite loop - only load if content is actually different
        const currentContent: string = this.editorContentRef.nativeElement.innerHTML
        if (currentContent !== newContent) {
          this.debugLog(COMPONENT_STRINGS.CHANGE_DETECTION_LOAD_REASON)
          this.loadContentIntoEditor(newContent)
        } else {
          this.debugLog(COMPONENT_STRINGS.CHANGE_DETECTION_SKIP_REASON)
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.ensureChangesAreSaved()

    // Cleanup event listeners
    if (this.cleanupEventListeners) {
      this.cleanupEventListeners()
      this.cleanupEventListeners = null
    }

    // Stop all cursor preservation observers
    this.editorInteractionService.stopAll()

    this.destroy$.next()
    this.destroy$.complete()
  }

  /**
   * Ensures all pending changes in tag editor are saved
   * Call this method before form submission or component destruction
   * to prevent data loss from inline tag editing
   */
  public ensureChangesAreSaved(): void {
    const inlineEditCallbacks: InlineEditCallbacks = this.createInlineEditCallbacks()
    this.inlineEditService.ensureChangesAreSaved(inlineEditCallbacks)
  }

  // ========== ControlValueAccessor ==========

  writeValue(value: string): void {
    const normalizedValue: string = value || ''
    this.debugLog(' writeValue called:', {
      valueLength: normalizedValue.length,
      hasEditor: !!this.editorContentRef,
      isEditorInitialized: this.stateService.getEditorInitialized(),
      isLoadingContent: this.stateService.getLoadingContent(),
      isEditingTag: !!this.stateService.getEditingTag(),
      currentValueLength: this.value.length,
    })

    // Get current editor content for comparison
    const currentContent: string = this.editorContentRef ? this.getEditorHTML() : this.value
    const isNewContent: boolean = normalizedValue !== currentContent && normalizedValue !== this.value

    // Only skip if content is same AND tag is being edited (user is actively editing)
    if (this.stateService.getEditingTag() && !isNewContent) {
      this.debugLog('  Skipping writeValue - tag is being edited and content unchanged')
      return
    }

    // If content is new, reset edit state to allow loading
    // This handles case when opening detail with existing content
    if (isNewContent && this.stateService.getEditingTag()) {
      this.debugLog('  New content detected - resetting edit state to allow loading')
      // Simply reset state - don't call exitInlineEditMode to avoid side effects
      this.stateService.setEditingTag(null)
      this.stateService.setPendingContent(null)
    }

    // Prevent feedback loop during content loading - but allow if content is different
    if (this.stateService.getLoadingContent()) {
      if (normalizedValue === currentContent) {
        this.debugLog('  Skipping writeValue - already loading same content')
        return
      }
      // Content is different - allow loading (will reset loading flag)
      this.debugLog('  Allowing writeValue - content is different')
    }

    this.value = normalizedValue

    // Always try to load content if editor is available and content is provided
    if (this.editorContentRef && normalizedValue) {
      if (this.stateService.getEditorInitialized()) {
        // Editor is initialized - load content directly
        this.debugLog(' Loading content via writeValue (editor initialized)')
        this.loadContentIntoEditor(normalizedValue)
        // Force validation a change detection po načtení hodnoty
        // onChange() by měl automaticky spustit validaci, ale explicitní markForCheck() zajistí aktualizaci UI
        if (this.onChange) {
          this.onChange(normalizedValue)
        }
        this.cdr.markForCheck()
      } else {
        // Editor not yet initialized - store for later and initialize
        this.debugLog(' Storing content for later loading and initializing editor')
        this.initialContent = normalizedValue
        this.initializeEditor()
      }
    } else if (normalizedValue && !this.editorContentRef) {
      // Content provided but editor not available - store for later
      this.debugLog(' Storing content for later loading (editor not available)')
      this.initialContent = normalizedValue
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.debugLog('setDisabledState called:', { isDisabled, hasEditor: !!this.editorContentRef })
    this.disabled = isDisabled
    if (this.editorContentRef) {
      const editor: HTMLDivElement = this.editorContentRef.nativeElement
      editor.contentEditable = isDisabled ? HTML_VALUES.CONTENTEDITABLE_FALSE : HTML_VALUES.CONTENTEDITABLE_TRUE
      this.debugLog('Editor contentEditable set to:', editor.contentEditable)
    }
  }

  // ========== Validator ==========

  validate(control: AbstractControl): ValidationErrors | null {
    // Pokud validace není povinná, vracet null
    if (!this.requireTags) {
      return null
    }

    const htmlContent = control.value || ''
    if (!htmlContent.trim()) {
      return { tagRequired: true }
    }

    // Parse tags from HTML string
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlContent
    const tags = this.tagManagerService.parseTagsFromContent(tempDiv)

    if (tags.length < TAG_EDITOR_THRESHOLDS.MIN_TAGS_COUNT) {
      return { tagRequired: true }
    }

    return null
  }

  private initializeEditor(): void {
    this.debugLog(' Initializing editor')

    if (!this.editorContentRef) {
      this.debugLog(' Editor content ref not available')
      return
    }

    this.stateService.setEditorInitialized(true)

    // Ensure editor is editable
    this.editorContentRef.nativeElement.contentEditable = HTML_VALUES.CONTENTEDITABLE_TRUE
    this.debugLog(' Editor contentEditable set to:', this.editorContentRef.nativeElement.contentEditable)

    // Setup event listeners
    this.setupEventListeners()

    // Load initial content if provided
    if (this.initialContent) {
      this.debugLog(' Loading initial content in initializeEditor')
      this.loadContentIntoEditor(this.initialContent)
      // Explicitly trigger validation after loading content
      // This ensures validation runs even if emitChange doesn't fire immediately
      if (this.onChange && this.initialContent) {
        // Use setTimeout to ensure content is loaded before validation
        setTimeout(() => {
          this.onChange(this.initialContent)
        }, 0)
      }
    } else if (this.value) {
      this.debugLog(' Loading value in initializeEditor')
      this.loadContentIntoEditor(this.value)
      // Explicitly trigger validation after loading content
      if (this.onChange && this.value) {
        setTimeout(() => {
          this.onChange(this.value)
        }, 0)
      }
    }
  }

  private createEventCallbacks(): EventCallbacks {
    return {
      onContentChange: (_content: string) => {
        this.onEditorInput()
      },
      onTagEdit: (tagElement: HTMLElement, clickRange?: Range) => {
        this.enterInlineEditMode(tagElement, clickRange)
      },
      onTagEditExit: (save: boolean) => {
        this.exitInlineEditMode(save)
      },
      onSelectionChange: () => {
        this.updateTextSelectionState()
      },
      onFormattingChange: () => {
        this.updateFormattingState()
      },
      onTagInsert: (node: TagEditorTreeNode, text: string) => {
        this.insertTag(node, text)
      },
      onTagRemove: (tagId: string) => {
        this.removeTag(tagId)
      },
      onTagClick: (tag: InsertedTagCustom) => {
        this.onTagClick(tag)
      },
      onTagInEditorClick: (tagId: string) => {
        this.highlightTagInSidebar(tagId)
      },
      onSaveButtonClick: () => {
        // Save button click handled by blur listener
      },
      onContentUpdate: (content: string) => {
        this.emitChangeSilent(content)
      },
      onTagsParse: () => {
        this.parseAndUpdateTags()
      },
      getEditorHTML: () => {
        return this.getEditorHTML()
      },
      getSelectedText: () => {
        return this.editorInteractionService.getSelectedText()
      },
      debouncedEmitChange: (content: string) => {
        this.debouncedEmitChange(content)
      },
      onBlurSave: () => {
        if (this.stateService.getLoadingContent()) {
          return
        }
        // Safety check: skip if tag selector is open or tag insertion is in progress
        if (this.stateService.getTagSelectorOpen() || this.stateService.getInsertingTag()) {
          this.debugLog(' onBlurSave skipped (tag selector open or inserting tag)')
          return
        }
        const content: string = this.getEditorHTML()
        if (this.hasContentChanged(content)) {
          this.value = content
          this.parseAndUpdateTags()
          this.emitChange(content)
        }
      },
      getTreeNodes: () => {
        return this.treeNodes
      },
    }
  }

  private createInlineEditCallbacks(): InlineEditCallbacks {
    return {
      onEditStart: (_tagElement: HTMLElement) => {
        // Edit start callback - can be extended if needed
      },
      onEditEnd: (_tagElement: HTMLElement, _save: boolean) => {
        // Edit end callback - can be extended if needed
      },
      onContentUpdate: (content: string) => {
        this.emitChangeSilent(content)
      },
      onStateUpdate: () => {
        this.cdr.markForCheck()
      },
      getEditorHTML: () => {
        return this.getEditorHTML()
      },
      onTagsParse: () => {
        this.parseAndUpdateTags()
      },
      onEmitChange: (content: string) => {
        this.emitChange(content)
      },
      onPendingContentSet: (content: string) => {
        this.stateService.setPendingContent(content)
      },
    }
  }

  private createChangeCallbacks(): ChangeCallbacks {
    return {
      onValueChange: (value: string) => {
        this.value = value
        // Update form control immediately to ensure value is propagated synchronously
        if (this.onChange) {
          this.onChange(value)
        }
      },
      onTouched: () => {
        this.onTouched()
      },
      onValueChangeEvent: (value: string) => {
        this.valueChange.emit(value)
      },
      onTagDataChangeEvent: (content: string, tags: ReadonlyArray<InsertedTagCustom>) => {
        this.tagDataChange.emit({
          content,
          tags,
        })
      },
      onStoreUpdate: (_hasTags: boolean) => {
        // Store update no longer needed - validation is handled by Validator interface
      },
    }
  }

  /**
   * Sets up all event listeners for the editor
   */
  private setupEventListeners(): void {
    if (!this.editorContentRef) {
      this.debugLog(' No editor content ref for event listeners')
      return
    }

    const editor = this.editorContentRef.nativeElement
    const eventCallbacks: EventCallbacks = this.createEventCallbacks()

    const editorContext: EditorContext = {
      editor,
      stateService: this.stateService,
      tagManagerService: this.tagManagerService,
      formattingService: this.formattingService,
      pasteService: this.pasteService,
      editorInteractionService: this.editorInteractionService,
      callbacks: eventCallbacks,
      isSelectingText: { value: this.isSelectingText },
    }

    this.cleanupEventListeners = this.eventService.setupEventListeners(editorContext)
    this.debugLog(' Event listeners setup completed')
  }

  /**
   * Loads content into the editor and parses existing tags
   */
  private loadContentIntoEditor(content: string): void {
    if (!this.editorContentRef) {
      return
    }

    const editor = this.editorContentRef.nativeElement
    const contentCallbacks: ContentCallbacks = {
      onContentLoaded: (_content: string) => {
        // Content loaded callback - can be extended if needed
      },
      onTagsUpdated: (tags: ReadonlyArray<InsertedTagCustom>) => {
        this.insertedTags = [...tags]
        this.stateService.setParsedTags(this.insertedTags)
        // Validation is now handled by Validator interface, no need to update state
      },
      onMarkForCheck: () => {
        this.cdr.markForCheck()
      },
      onEmitChange: (content: string) => {
        this.emitChange(content)
      },
    }

    this.contentService.loadContent(editor, content, contentCallbacks, {
      debug: this.debug,
      parseTags: true,
      emitChange: true,
    })
  }

  /**
   * Handles editor input events
   */
  private onEditorInput(): void {
    if (this.stateService.getLoadingContent()) {
      return
    }

    const content = this.getEditorHTML()
    this.debugLog(' Editor input detected:', { contentLength: content.length })

    const contentChanged = this.hasContentChanged(content)
    this.value = content

    const editingTag = this.stateService.getEditingTag()
    if (editingTag) {
      this.ngZone.runOutsideAngular(() => {
        this.emitChangeSilent(content)
        this.stateService.setPendingContent(content)
      })
      return
    }

    this.debouncedEmitChange(content)

    if (contentChanged) {
      this.parseAndUpdateTags()
    }
  }

  protected applyBold(): void {
    this.formattingService.applyBold()
    this.updateFormattingState()
  }

  protected applyItalic(): void {
    this.formattingService.applyItalic()
    this.updateFormattingState()
  }

  protected applyUnderline(): void {
    this.formattingService.applyUnderline()
    this.updateFormattingState()
  }

  protected insertOrderedList(): void {
    if (!this.editorContentRef) {
      return
    }
    const success: boolean = this.formattingService.insertOrderedList()
    if (success) {
      // Restore focus to editor after formatting
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          if (this.editorContentRef) {
            this.editorContentRef.nativeElement.focus()
          }
        })
      })
    }
    this.updateFormattingState()
  }

  protected insertUnorderedList(): void {
    if (!this.editorContentRef) {
      return
    }
    const success: boolean = this.formattingService.insertUnorderedList()
    if (success) {
      // Restore focus to editor after formatting
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          if (this.editorContentRef) {
            this.editorContentRef.nativeElement.focus()
          }
        })
      })
    }
    this.updateFormattingState()
  }

  protected insertLink(): void {
    const urlPrompt: string = this.translocoService.translate('TAG_EDITOR.URL_PROMPT') as string
    const url: string | null = prompt(urlPrompt)
    if (url && url.trim() !== '') {
      this.formattingService.createLink(url.trim())
      this.updateFormattingState()
    }
  }

  protected removeFormat(): void {
    this.formattingService.removeFormat()
    this.updateFormattingState()
  }

  protected updateFormattingState(): void {
    this.formattingState = this.uiStateService.getFormattingState()
    this.cdr.markForCheck()
  }

  private updateTextSelectionState(): void {
    if (!this.editorContentRef) {
      return
    }
    const editor = this.editorContentRef.nativeElement
    const state = { value: this.isTextSelected }
    const updated = this.uiStateService.updateTextSelectionState(editor, state, {
      onMarkForCheck: () => {
        this.cdr.markForCheck()
      },
    })
    if (updated) {
      this.isTextSelected = state.value
    }
  }

  private createSelectorCallbacks(): SelectorCallbacks {
    return {
      onOpen: () => {
        this.showTagSelector = true
        this.cdr.markForCheck()
      },
      onClose: () => {
        this.showTagSelector = false
        this.cdr.markForCheck()
      },
      onNodeSelect: (node: TagEditorTreeNode, selectedText: string) => {
        this.insertTag(node, selectedText)
      },
      onSelectionValidate: (selectedText: string) => {
        return !!(selectedText && selectedText.trim().length > 0)
      },
      onMarkForCheck: () => {
        this.cdr.markForCheck()
      },
    }
  }

  protected openTagSelector(): void {
    if (!this.editorContentRef) {
      return
    }
    const editor = this.editorContentRef.nativeElement
    const selectorCallbacks: SelectorCallbacks = this.createSelectorCallbacks()
    this.selectorService.openSelector(editor, selectorCallbacks)
  }

  protected closeTagSelector(): void {
    const selectorCallbacks: SelectorCallbacks = this.createSelectorCallbacks()
    this.selectorService.closeSelector(selectorCallbacks)
  }

  protected onTreeNodeSelect(event: { node: TreeNode }): void {
    if (!this.editorContentRef || !event.node) {
      return
    }
    const node: TagEditorTreeNode = event.node as unknown as TagEditorTreeNode
    const editor = this.editorContentRef.nativeElement
    const selectorCallbacks: SelectorCallbacks = {
      ...this.createSelectorCallbacks(),
      onSelectionValidate: (selectedText: string) => {
        if (!selectedText || selectedText.trim().length === 0) {
          const alertMessage: string = this.translocoService.translate('TAG_EDITOR.NO_TEXT_SELECTED') as string
          alert(alertMessage)
          return false
        }
        return true
      },
    }
    this.selectorService.handleNodeSelect(node, editor, selectorCallbacks)
  }

  protected onTreeNodeUnselect(event: { node: TreeNode | null }): void {
    if (event.node) {
      const node: TagEditorTreeNode = event.node as unknown as TagEditorTreeNode
      this.debugLog('Tree node unselected:', node.label)
    }
  }

  /**
   * Inserts a tag element at the current selection
   */
  private insertTag(node: TagEditorTreeNode, text: string): void {
    if (!this.editorContentRef) {
      return
    }

    const existingEditingTag = this.stateService.getEditingTag()
    if (existingEditingTag) {
      this.debugLog(' Exiting existing edit mode before inserting new tag')
      this.exitInlineEditMode(true)
    }

    const editor = this.editorContentRef.nativeElement
    const insertionCallbacks: InsertionCallbacks = {
      onBeforeInsert: () => {
        this.exitInlineEditMode(true)
      },
      onAfterInsert: (_tagElement: HTMLElement) => {
        // After insert callback - can be extended if needed
      },
      onTagsUpdated: (tags: ReadonlyArray<InsertedTagCustom>) => {
        this.insertedTags = [...tags]
        this.stateService.setParsedTags(this.insertedTags)
        // Validation is now handled by Validator interface, no need to update state
        this.cdr.markForCheck()
      },
      onContentChanged: (content: string) => {
        // Update value immediately and emit change
        this.value = content
        // Emit change which will update form control via onValueChange callback
        this.emitChange(content)
      },
      onDebouncedContentChanged: (content: string) => {
        this.debouncedEmitChange(content)
      },
      onMarkForCheck: () => {
        this.cdr.markForCheck()
      },
      getEditorHTML: () => {
        return this.getEditorHTML()
      },
    }

    this.insertionService.insertTag(node, text, editor, insertionCallbacks)
  }

  public onTagClick(tag: InsertedTagCustom | null): void {
    if (!this.editorContentRef || !tag) {
      return
    }

    const tagElement: HTMLElement | null = this.tagManagerService.findTagById(
      this.editorContentRef.nativeElement,
      tag.id,
    )

    if (tagElement) {
      this.tagManagerService.highlightTag(tagElement)
    }
  }

  /**
   * Highlights corresponding tag in sidebar when tag is clicked in editor
   */
  private highlightTagInSidebar(tagId: string): void {
    if (!this.insertedTags.length) {
      return
    }

    const tag: InsertedTagCustom | undefined = this.insertedTags.find((t) => t.id === tagId)
    if (!tag) {
      return
    }

    // Update selected tag to trigger highlight
    this.selectedTag = tag
    this.cdr.markForCheck()

    // Scroll to highlighted item in listbox
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.tagListbox?.el.nativeElement) {
          const listboxContainer: HTMLElement = this.tagListbox.el.nativeElement
          const selectedItem: HTMLElement | null = listboxContainer.querySelector('.p-listbox-option-selected')
          if (selectedItem) {
            selectedItem.scrollIntoView({
              behavior: CSS_STYLE_VALUES.SCROLL_BEHAVIOR as ScrollBehavior,
              block: CSS_STYLE_VALUES.SCROLL_BLOCK as ScrollLogicalPosition,
              inline: CSS_STYLE_VALUES.SCROLL_INLINE as ScrollLogicalPosition,
            })
          }
        }
      }, TAG_EDITOR_DELAY.FOCUS_DELAY_MS)
    })
  }

  public removeTag(tagId: string): void {
    this.debugLog(' removeTag called:', { tagId })

    if (!this.editorContentRef) {
      this.debugLog(' Cannot remove tag - editor ref not available')
      return
    }

    const tagElement: HTMLElement | null = this.tagManagerService.findTagById(
      this.editorContentRef.nativeElement,
      tagId,
    )

    if (!tagElement) {
      this.debugLog(' Tag element not found:', tagId)
      return
    }

    this.debugLog(' Removing tag element from DOM')
    this.tagManagerService.removeTagElement(tagElement)

    // Update tags list and emit change (use requestAnimationFrame to ensure DOM is updated)
    requestAnimationFrame(() => {
      this.parseAndUpdateTags()
      this.emitChange(this.getEditorHTML())
      // Force change detection to update sidebar
      this.cdr.markForCheck()
      this.debugLog(' Tag removed successfully, tags count:', this.insertedTags.length)
    })
  }

  // ========== Inline Tag Editing ==========

  /**
   * Enters inline edit mode for a tag element
   */
  private enterInlineEditMode(tagElement: HTMLElement, clickRange?: Range): void {
    const inlineEditCallbacks: InlineEditCallbacks = this.createInlineEditCallbacks()
    this.inlineEditService.enterEditMode(tagElement, inlineEditCallbacks, clickRange)
  }

  /**
   * Exits inline edit mode and optionally saves changes
   */
  private exitInlineEditMode(save: boolean): void {
    const inlineEditCallbacks: InlineEditCallbacks = this.createInlineEditCallbacks()
    this.inlineEditService.exitEditMode(save, inlineEditCallbacks)
  }

  private getEditorHTML(): string {
    if (!this.editorContentRef) {
      return ''
    }
    return this.editorContentRef.nativeElement.innerHTML
  }

  /**
   * Copies editor HTML content to clipboard as plain text for debugging
   */
  protected copyEditorHtmlToClipboard(): void {
    const htmlContent: string = this.getEditorHTML()
    this.pasteService.copyHtmlAsPlainText(htmlContent)
    this.debugLog(' Copied HTML content to clipboard as plain text:', {
      contentLength: htmlContent.length,
    })
  }

  /**
   * Checks if content has actually changed to avoid unnecessary parsing
   * during navigation events (arrow keys, etc.)
   */
  private hasContentChanged(newContent: string): boolean {
    return this.value !== newContent
  }

  /**
   * Parses tags from editor content and updates component state
   * Never called during inline tag editing to prevent cursor loss
   */
  private parseAndUpdateTags(): void {
    if (this.stateService.getEditingTag()) {
      this.debugLog(' Skipping parseAndUpdateTags - tag is being edited inline')
      return
    }

    if (!this.editorContentRef) {
      this.debugLog(' No editor content ref for parsing')
      return
    }

    const editor: HTMLDivElement = this.editorContentRef.nativeElement
    this.debugLog(
      ' Parsing tags from editor HTML:',
      editor.innerHTML.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
    )

    // Normalize tag structure before parsing to ensure consistency
    const normalizedCount: number = this.tagManagerService.normalizeTagStructure(editor)
    if (normalizedCount > 0) {
      this.debugLog(' Normalized tags before parsing:', normalizedCount)
    }

    // DEBUG: Log full HTML content if debug mode is enabled
    if (this.debug) {
      this.debugLog(' DEBUG: Full HTML content during parsing:')
      this.debugLog(editor.innerHTML)
      this.debugLog(' DEBUG: HTML content ends here')
    }

    const parsedTags: ReadonlyArray<InsertedTagCustom> = this.tagManagerService.parseTagsFromContent(editor)
    // Create new array reference to trigger Angular change detection
    this.insertedTags = [...parsedTags]
    // Update state service with parsed tags
    this.stateService.setParsedTags(this.insertedTags)

    this.debugLog(' Parsed tags:', {
      count: this.insertedTags.length,
      tags: this.insertedTags.map((t) => ({
        id: t.id,
        label: t.label,
        text: t.text.substring(0, TAG_EDITOR_VALUES.TAG_TEXT_PREVIEW_LENGTH),
      })),
    })

    // Force change detection to update UI (OnPush strategy)
    // Validation is now handled by Validator interface, no need to update state
    this.cdr.markForCheck()
  }

  /**
   * Silently updates internal value without triggering change detection
   */
  private emitChangeSilent(content: string): void {
    const changeCallbacks: ChangeCallbacks = this.createChangeCallbacks()
    this.changeService.emitChangeSilent(content, changeCallbacks)
  }

  /**
   * Emits changes to Angular forms and parent components
   */
  private emitChange(content: string): void {
    const changeCallbacks: ChangeCallbacks = this.createChangeCallbacks()
    this.changeService.emitChange(content, changeCallbacks)
    // Update store with tags (after parsing)
    const hasTags: boolean = this.insertedTags.length >= TAG_EDITOR_THRESHOLDS.MIN_TAGS_COUNT
    if (changeCallbacks.onStoreUpdate) {
      changeCallbacks.onStoreUpdate(hasTags)
    }
  }

  protected getIpJournalUrl(): string {
    if (!this.clientId) {
      this.debugLog(' getIpJournalUrl: clientId is null, returning #')
      return TAG_EDITOR_STRINGS.HASH_SYMBOL
    }
    const url: string = `${TAG_EDITOR_STRINGS.IP_JOURNAL_URL_PREFIX}${this.clientId}${TAG_EDITOR_STRINGS.IP_JOURNAL_URL_SUFFIX}`
    this.debugLog(' getIpJournalUrl:', { clientId: this.clientId, url })
    return url
  }
}
